<script setup lang="ts">
import { onBeforeMount, ref, toRefs } from 'vue';
import InputMask from 'primevue/inputmask';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useRouting } from '@/composables';
import { ReferralBuilderService } from '@/services';
import { ReferralBuilderSettingsInterface, ReferralBuilderProcessPayloadInterface } from '@/types';

interface PropsInterface {
  referralFormId?: string;
  isPreview?: boolean;
}

const props = withDefaults(defineProps<PropsInterface>(), {
  referralFormId: '',
  isPreview: false,
});

const { referralFormId } = toRefs(props);

const $routing = useRouting();

const loading = ref(true);

let v$ = useVuelidate();

const data = ref({
  student_id: '',
  student_first_name: '',
  student_last_name: '',
  building_campus: '',
  grade: '',
  referral_reason_id: '',
  referrer_title: '',
  referrer_first_name: '',
  referrer_last_name: '',
  referrer_email: '',
  referrer_phone: '',
  referral_code: '',
  referral_form_id: '',
  send_email: false,
});

const referralBuilderSettings = ref();

const referralReasons = ref([]);

const grades = ref([]);

const organization = ref(null);

const requestError = ref('');

const processingErrors = ref();

const processingSuccess = ref();

onBeforeMount(() => init());

const init = async () => {
  defineReferralFormId();

  if (data.value.referral_form_id?.length) {
    await ReferralBuilderService.getReferralBuilderSettingsByCode(data.value.referral_form_id.toString())
      .then((response) => {
        const settings = response.data.data.referral_settings;
        organization.value = response.data.data.organization_name;
        defineSettings(settings);
        referralReasons.value = response.data.data.referral_reasons;
        grades.value = response.data.data.grades;
        defineValidation(settings);
        loading.value = false;
      })
      .catch((error) => {
        requestError.value = error.data.message;
        loading.value = false;
      });
  }
};

const defineReferralFormId = () => {
  // eslint-disable-next-line
  // @ts-ignore
  data.value.referral_form_id = referralFormId.value?.length
    ? referralFormId.value.toString()
    : $routing.route.query.code;
};

const defineSettings = async (settings: ReferralBuilderSettingsInterface[]) => {
  referralBuilderSettings.value = new Map();
  settings.forEach((values: ReferralBuilderSettingsInterface) => referralBuilderSettings.value.set(values.id, values));
};

const defineValidation = async (settings: ReferralBuilderSettingsInterface[]) => {
  const rules = {};

  settings.forEach((item: ReferralBuilderSettingsInterface) => {
    const ignore = ['organization'];

    if (!ignore.includes(item.id)) {
      // eslint-disable-next-line
      // @ts-ignore
      rules[item.id] = item.required.enabled ? { required } : { required: false };
    }
  });

  v$ = useVuelidate(rules, data);
};

const toInclude = (id: string) => referralBuilderSettings.value.get(id).include.enabled;

const isRequired = (id: string) => ({ required: referralBuilderSettings.value.get(id).required.enabled });

const onClear = () => {
  data.value = {
    student_id: '',
    student_first_name: '',
    student_last_name: '',
    building_campus: '',
    grade: '',
    referral_reason_id: '',
    referrer_title: '',
    referrer_first_name: '',
    referrer_last_name: '',
    referrer_email: '',
    referrer_phone: '',
    referral_code: '',
    send_email: false,
  };
  v$.value.$reset();
  defineReferralFormId();
};

const onSubmit = async () => {
  if (!(await v$.value.$validate())) return;

  const payload: ReferralBuilderProcessPayloadInterface = data.value;
  processingSuccess.value = '';
  processingErrors.value = '';

  ReferralBuilderService.processReferralIntake(payload)
    .then((response) => {
      processingSuccess.value = response.data.message;
    })
    .catch((error) => {
      if (!error.data?.errors) {
        processingErrors.value = error.data.message;
      } else {
        const errorMessages: string[] = [];
        const errorValues = Object.values(error.data.errors);
        errorValues.forEach((items) => items.forEach((item: string) => errorMessages.push(item)));
        processingErrors.value = errorMessages;
      }
    });
};
</script>

<template>
  <div v-if="!loading" class="m-auto">
    <Card v-if="requestError.length" class="m-auto w-auto border-4 border-primary-100 bg-gray-50">
      <template #content>
        <div class="p-4 text-center">
          <span class="font-semibold text-primary text-lg" v-text="requestError" />
        </div>
      </template>
    </Card>
    <Card v-else class="m-auto w-auto border-4 border-primary-100 bg-gray-50">
      <template #content>
        <div class="pt-4 pr-2 pb-0 pl-2">
          <div class="grid pb-2">
            <div v-if="toInclude('organization')" class="col">
              <h1 class="pt-2 pb-2 font-semibold text-2xl" v-text="organization" />
            </div>
            <div class="col-fixed">
              <div class="grid">
                <div class="col-fixed">
                  <img alt="eLuma logo" height="36" src="@/assets/eluma-logo.svg" />
                </div>
                <div class="col-fixed">
                  <img alt="eLuma logo" height="20" class="mt-2" src="@/assets/eluma-text.svg" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="processingErrors" class="grid pb-4 fadein animation-duration-500">
            <div class="col p-4 p-message-error border-round-md">
              <span v-if="typeof processingErrors === 'string'" v-text="processingErrors" />
              <template v-else>
                <h3 class="mb-3">Please correct the following errors.</h3>
                <ul>
                  <li v-for="(error, key) in processingErrors" :key="key" class="pb-1" v-text="error" />
                </ul>
              </template>
            </div>
          </div>
          <div v-if="processingSuccess" class="grid pb-4 fadein animation-duration-500">
            <div class="col p-4 p-message-success border-round-md">
              <h3 v-text="processingSuccess" />
            </div>
          </div>
          <div v-else>
            <div class="grid">
              <div v-if="toInclude('student_id')" class="field p-1 col">
                <label :class="isRequired('student_id')" for="student_id">SSID</label>
                <InputText
                  id="student_id"
                  v-model="v$.student_id.$model"
                  type="text"
                  placeholder="SSID"
                  class="w-full"
                  :class="{ 'w-full': true, 'p-invalid': v$.student_id.$error }"
                  autocomplete="off"
                />
              </div>
              <div v-if="toInclude('building_campus')" class="field p-1 col">
                <label :class="isRequired('building_campus')" for="building_campus">
                  District/School/Campus/Building
                </label>
                <InputText
                  id="building_campus"
                  v-model="v$.building_campus.$model"
                  type="text"
                  placeholder="District/School/Campus/Building"
                  :class="{ 'w-full': true, 'p-invalid': v$.building_campus.$error }"
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="grid">
              <div v-if="toInclude('student_first_name')" class="field p-1 col">
                <label :class="isRequired('student_first_name')" for="student_first_name">First Name</label>
                <InputText
                  id="student_first_name"
                  v-model="v$.student_first_name.$model"
                  type="text"
                  placeholder="First Name"
                  :class="{ 'w-full': true, 'p-invalid': v$.student_first_name.$error }"
                  autocomplete="off"
                />
              </div>
              <div v-if="toInclude('student_last_name')" class="field p-1 col">
                <label :class="isRequired('student_last_name')" for="student_last_name">Last Name</label>
                <InputText
                  id="student_last_name"
                  v-model="v$.student_last_name.$model"
                  type="text"
                  placeholder="Last Name"
                  :class="{ 'w-full': true, 'p-invalid': v$.student_last_name.$error }"
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="grid">
              <div v-if="toInclude('grade')" class="field p-1 col">
                <label :class="isRequired('grade')" for="grade">Grade</label>
                <Dropdown
                  id="grade"
                  v-model="v$.grade.$model"
                  :options="grades"
                  placeholder="Select Grade"
                  :class="{ 'w-full': true, 'p-invalid': v$.grade.$error }"
                />
              </div>
              <div v-if="toInclude('referral_reason_id')" class="field p-1 col">
                <label :class="isRequired('referral_reason_id')" for="referral_reason_id">Reason</label>
                <Dropdown
                  id="referral_reason_id"
                  v-model="v$.referral_reason_id.$model"
                  :options="referralReasons"
                  option-label="title"
                  option-value="id"
                  placeholder="Select Reason"
                  :class="{ 'w-full': true, 'p-invalid': v$.referral_reason_id.$error }"
                />
              </div>
            </div>
            <div class="mt-3 mb-5 border-top-3 border-gray-200"></div>
            <div class="grid">
              <div v-if="toInclude('referrer_title')" class="field p-1 col">
                <label :class="isRequired('referrer_title')" for="referrer_title">
                  Referrer Title/Relationship to Referral
                </label>
                <InputText
                  id="referrer_title"
                  v-model="v$.referrer_title.$model"
                  type="text"
                  placeholder="Referrer Title/Relationship to Referral"
                  :class="{ 'w-full': true, 'p-invalid': v$.referrer_title.$error }"
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="grid">
              <div v-if="toInclude('referrer_first_name')" class="field p-1 col">
                <label :class="isRequired('referrer_first_name')" for="referrer_first_name">Referrer First Name</label>
                <InputText
                  id="referrer_first_name"
                  v-model="v$.referrer_first_name.$model"
                  type="text"
                  placeholder="Referrer First Name"
                  :class="{ 'w-full': true, 'p-invalid': v$.referrer_first_name.$error }"
                  autocomplete="off"
                />
              </div>
              <div v-if="toInclude('referrer_last_name')" class="field p-1 col">
                <label :class="isRequired('referrer_last_name')" for="referrer_last_name">Referrer Last Name</label>
                <InputText
                  id="referrer_last_name"
                  v-model="v$.referrer_last_name.$model"
                  type="text"
                  placeholder="Referrer Last Name"
                  :class="{ 'w-full': true, 'p-invalid': v$.referrer_last_name.$error }"
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="grid">
              <div v-if="toInclude('referrer_email')" class="field p-1 col">
                <label :class="isRequired('referrer_email')" for="referrer_email">Referrer Email</label>
                <InputText
                  id="referrer_email"
                  v-model="v$.referrer_email.$model"
                  type="text"
                  placeholder="Referrer Email"
                  :class="{ 'w-full': true, 'p-invalid': v$.referrer_email.$error }"
                  autocomplete="off"
                />
              </div>
              <div v-if="toInclude('referrer_phone')" class="field p-1 col">
                <label :class="isRequired('referrer_phone')" for="referrer_phone">Referrer Phone</label>
                <InputMask
                  id="referrer_phone"
                  v-model="v$.referrer_phone.$model"
                  mask="(999) 999-9999"
                  placeholder="(999) 999-9999"
                  :class="{ 'w-full': true, 'p-invalid': v$.referrer_phone.$error }"
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="mt-3 mb-5 border-top-3 border-gray-200"></div>
            <div class="grid">
              <div v-if="toInclude('referral_code')" class="field p-1 pb-0 col">
                <label :class="isRequired('referral_code')" for="referral_code">Referral Code</label>
                <InputText
                  id="referral_code"
                  v-model="v$.referral_code.$model"
                  type="text"
                  placeholder="Referral Code"
                  :class="{ 'w-full': true, 'p-invalid': v$.referral_code.$error }"
                  autocomplete="off"
                />
              </div>
              <div v-if="toInclude('send_email')" class="field col">
                <div class="py-4 pt-5 pl-5">
                  <Checkbox id="send_email" v-model="v$.send_email.$model" class="mr-2" :binary="true" />
                  <label :class="`font-semibold text-primary ${isRequired('send_email')}`" for="send_email">
                    Send Confirmation Email
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div v-if="!processingSuccess" class="flex justify-content-end pb-2 pl-2 pr-2">
          <AppButton type="secondary" text="Clear" @click="onClear" />
          <AppButton type="primary" text="Submit" :disabled="isPreview" @click="onSubmit" />
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped lang="scss">
:deep(.p-card-content) {
  padding-bottom: 0;

  .p-message-success {
    background: $successMessageBg;
    color: $successMessageTextColor;
    font-size: 16px !important;
  }

  .p-message-error {
    background: $errorMessageBg;
    color: $errorMessageTextColor;
    font-size: 16px !important;
  }
}
</style>
